import * as React from 'react';
import ReactHotToast from 'react-hot-toast';

import { trackEvent } from '@/analytics/trackEvent';
import { axiosInstance } from '@/axios';
import { useCreateNotes } from '@/features/Notes/hooks/useCreateNotes';
import { getSnipData } from '@/features/Notes/NoteSelector/getSnipDetails';
import { getSnipJSON } from '@/features/Notes/NoteSelector/getSnipJSON';
import type { Notebook } from '@/features/Notes/types';
import type { FeedItemType } from '@/types';
import { CommonErrorToast } from '@/utils/toasts';

export const useCreateNotesHandler = ({
  result,
  currentParagraph,
}: UseCreateNotesHandlerType) => {
  const createNotesMutation = useCreateNotes();

  const getPublicAccessKey = React.useCallback(async (result: FeedItemType) => {
    try {
      const { data } = await axiosInstance.get<{ results: FeedItemType[] }>(
        `/doc/${result.source}`,
        {
          params: {
            deeplink: result.deeplink,
            access_key: result.access_key,
            share: true,
          },
        }
      );
      return data.results[0].public_access_key;
    } catch (e) {
      console.error(e); // for debugging (TODO: remove after stable)
    }
  }, []);

  return React.useCallback(
    async (notebooks: Notebook[]) => {
      const snipData = getSnipData(result, currentParagraph);
      const publicAccessKey = await getPublicAccessKey(result);

      if (!publicAccessKey) {
        CommonErrorToast();
        return;
      }

      const snipJSON = getSnipJSON({ ...snipData, publicAccessKey });

      createNotesMutation.mutate(
        {
          notebooks,
          document: snipJSON,
          title: snipData.title,
        },
        {
          onSuccess: () => {
            trackEvent('note_snipped', { source: snipData.source });
            ReactHotToast.success('Note created successfully!');
          },
          onError: () => {
            ReactHotToast.error('Add to Notes failed! Please contact us!');
          },
        }
      );
    },
    [createNotesMutation, currentParagraph, getPublicAccessKey, result]
  );
};

type UseCreateNotesHandlerType = {
  result: FeedItemType;
  currentParagraph: number;
};
